import { useMemo } from "react";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import {
    AXNUIAsyncWaitingPage,
    AXNUIErrorPage,
    AXNUIMainContainer,
    AXNUIMobileCompletePage,
    AXNUISuccessPage
} from "@iddataweb/idw-common-ui/containers";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent, AXNUILayout, AXNUITheme } from "@iddataweb/idw-common-ui/types";
import { getMatchesStaticRedirectUrl } from "@iddataweb/idw-common-ui/utils";
import { FormattedRpServiceConfigurationAttribute } from "@idw-types/data/rp-service-configuration";
import { AXNUIThemingPreviewConfigurationState } from "@idw-types/common/axnui/axnui-theming-preview-configuration-state";
import { AXNUIThemingLandingPage } from "../axnui-theming-landing-page/axnui-theming-landing-page";
import { KBAUIThemingVerificationForm } from "../kbaui-theming-verification-form/kbaui-theming-verification-form";
import { AXNUIThemingVerificationForm } from "../axnui-theming-verification-form/axnui-theming-verification-form";
import { AxnAttributeTypeField, AxnAttributeTypeFieldCustomization } from "@idw-types/data/reference-data/axn";

type AXNUIThemingMainContainerProps = {
    currentTab: AXNUIThemingTabTypes;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    attributes: Array<FormattedRpServiceConfigurationAttribute>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    localizeStrings: boolean;
    mergeThemingAndLocalizedFile: boolean;
    activeLanguage: LocaleTypes;
    hasCancelButtonURLEnabled: boolean;
    previewEntityType: PreviewEntityTypes;
    previewConfigurationState: AXNUIThemingPreviewConfigurationState;
    setPreviewConfigurationState: (state: AXNUIThemingPreviewConfigurationState) => void;
};

export const AXNUIThemingMainContainer = (props: AXNUIThemingMainContainerProps) => {
    const {
        currentTab,
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        attributes,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        activeLanguage,
        hasCancelButtonURLEnabled,
        previewEntityType,
        previewConfigurationState,
        setPreviewConfigurationState
    } = props;

    const shouldShowVerificationForm = useMemo(() => {
        return [
            AXNUIThemingTabTypes.ACTION_BUTTON,
            AXNUIThemingTabTypes.FOOTER,
            AXNUIThemingTabTypes.HEADER,
            AXNUIThemingTabTypes.LAYOUT,
            AXNUIThemingTabTypes.FORM_PANEL,
            AXNUIThemingTabTypes.PRE_FORM_CONTENT_SLOT,
            AXNUIThemingTabTypes.FORM_CONTENT,
            AXNUIThemingTabTypes.POST_FORM_CONTENT_SLOT,
            AXNUIThemingTabTypes.POST_SUBMIT_CONTENT_SLOT,
            AXNUIThemingTabTypes.CUSTOM_CSS,
            AXNUIThemingTabTypes.POLICY_RETRY_MESSAGE
        ].includes(currentTab);
    }, [currentTab]);

    const shouldShowSuccessPage = useMemo(() => {
        return [AXNUIThemingTabTypes.SUCCESS_PAGE].includes(currentTab) && axnuiLayout.isSuccessPageEnabled;
    }, [currentTab, axnuiLayout.isSuccessPageEnabled]);

    const shouldShowErrorPage = useMemo(() => {
        return [AXNUIThemingTabTypes.ERROR_PAGE].includes(currentTab);
    }, [currentTab]);

    const shouldShowLandingPage = useMemo(() => {
        return (
            [AXNUIThemingTabTypes.LANDING_PAGE].includes(currentTab) &&
            !!previewConfigurationState.selectedLandingPage
        );
    }, [currentTab, previewConfigurationState.selectedLandingPage]);

    const shouldShowAsyncWaitingPage = useMemo(() => {
        return [AXNUIThemingTabTypes.ASYNC_WAITING_PAGE].includes(currentTab);
    }, [currentTab]);

    const shouldShowMobileCompletePage = useMemo(() => {
        return [AXNUIThemingTabTypes.MOBILE_COMPLETE_PAGE].includes(currentTab);
    }, [currentTab]);

    const shouldShowKbaForm = useMemo(() => {
        return [AXNUIThemingTabTypes.KBA_FORM].includes(currentTab);
    }, [currentTab]);

    const isStaticLandingPage = useMemo(() => {
        return shouldShowLandingPage && getMatchesStaticRedirectUrl(previewConfigurationState.selectedLandingPage);
    }, [shouldShowLandingPage]);

    return (
        <AXNUIMainContainer
            apikey="axnui-preview-apikey"
            asi="axnui-preview-asi"
            generalTheme={axnuiTheme.general}
            isStaticLandingPage={isStaticLandingPage}
            useThemedUI={true}
        >
            <>
                {shouldShowVerificationForm && (
                    <AXNUIThemingVerificationForm
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        attributes={attributes}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                        localizeStrings={localizeStrings}
                        mergeThemingAndLocalizedFile={mergeThemingAndLocalizedFile}
                        activeLanguage={activeLanguage}
                        hasCancelButtonURLEnabled={hasCancelButtonURLEnabled}
                        previewEntityType={previewEntityType}
                        previewConfigurationState={previewConfigurationState}
                        setPreviewConfigurationState={setPreviewConfigurationState}
                        currentTab={currentTab}
                    />
                )}
                {shouldShowSuccessPage && (
                    <AXNUISuccessPage
                        content={axnuiContent}
                        formPanelLayout={null}
                        formPanelTheme={null}
                        idpName={null}
                        logoPosition={null}
                        logoUrl={null}
                        relyingPartyName={null}
                        useThemedUI={true}
                    />
                )}
                {shouldShowErrorPage && (
                    <AXNUIErrorPage
                        content={axnuiContent}
                        asi={"0000-abcd-1111-defg-2222"}
                        errorCode={previewConfigurationState.selectedErrorCode}
                        referenceId={previewConfigurationState.showErrorPageReferenceId ? "E34J3209KLOJ" : null}
                        errorMessage={null}
                        formPanelLayout={null}
                        formPanelTheme={null}
                        idpName={null}
                        logoPosition={null}
                        logoUrl={null}
                        relyingPartyName={null}
                        useThemedUI={true}
                    />
                )}
                {shouldShowLandingPage && (
                    <AXNUIThemingLandingPage
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        selectedLandingPage={previewConfigurationState.selectedLandingPage}
                        selectedLandingPagePolicyDecisionResultType={previewConfigurationState.selectedLandingPagePolicyDecisionResultType}
                    />
                )}
                {shouldShowAsyncWaitingPage && (
                    <AXNUIAsyncWaitingPage
                        axnuiContent={axnuiContent}
                        asyncModalMessage={"common.asyncModal.waiting"}
                    />
                )}
                {shouldShowMobileCompletePage && (
                    <AXNUIMobileCompletePage
                        axnuiContent={axnuiContent}
                        hasError={previewConfigurationState.showMobileCompleteErrorMessage}
                    />
                )}
                {shouldShowKbaForm && (
                    <KBAUIThemingVerificationForm
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        showKbaRetryMessage={previewConfigurationState.showKbaRetryMessage}
                    />
                )}
            </>
        </AXNUIMainContainer>
    );
};